import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CreditCard as CardIcon,
  QrCode as QrIcon,
  CreditScore as PayNowIcon,
  Key as ApiKeyIcon,
  LinkOff as DisconnectedIcon,
  Check as ConnectedIcon
} from '@mui/icons-material';
import SettingsDialogBase from './SettingsDialogBase';

const PaymentDialog = ({ open, onClose, currentMethods = [], onSave }) => {
  // State for payment methods
  const [paymentMethods, setPaymentMethods] = useState({
    cash: true,
    card: currentMethods.includes('Credit/Debit Card'),
    paynow: currentMethods.includes('PayNow QR'),
    stripe: currentMethods.includes('Stripe')
  });
  
  // State for integration settings
  const [stripeSettings, setStripeSettings] = useState({
    apiKey: '',
    webhookSecret: '',
    connected: false
  });
  
  const [paynowSettings, setPaynowSettings] = useState({
    merchantId: '',
    terminalId: '',
    companyName: '',
    connected: false
  });
  
  // Expanded panels state
  const [expanded, setExpanded] = useState(null);
  
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };
  
  const handlePaymentMethodToggle = (method) => (event) => {
    setPaymentMethods({
      ...paymentMethods,
      [method]: event.target.checked
    });
  };
  
  const handleStripeSettingChange = (field) => (event) => {
    setStripeSettings({
      ...stripeSettings,
      [field]: event.target.value
    });
  };
  
  const handlePaynowSettingChange = (field) => (event) => {
    setPaynowSettings({
      ...paynowSettings,
      [field]: event.target.value
    });
  };
  
  const handleStripeConnect = () => {
    // Simulate connection to Stripe API
    if (stripeSettings.apiKey.trim()) {
      setStripeSettings({
        ...stripeSettings,
        connected: true
      });
    }
  };
  
  const handlePaynowConnect = () => {
    // Simulate connection to PayNow API
    if (paynowSettings.merchantId.trim() && paynowSettings.terminalId.trim()) {
      setPaynowSettings({
        ...paynowSettings,
        connected: true
      });
    }
  };
  
  const handleSaveSettings = () => {
    // Prepare the final list of enabled payment methods
    const enabledMethods = [];
    if (paymentMethods.cash) enabledMethods.push('Cash');
    if (paymentMethods.card) enabledMethods.push('Credit/Debit Card');
    if (paymentMethods.paynow && paynowSettings.connected) enabledMethods.push('PayNow QR');
    if (paymentMethods.stripe && stripeSettings.connected) enabledMethods.push('Stripe');
    
    onSave(enabledMethods);
  };
  
  return (
    <SettingsDialogBase
      open={open}
      onClose={onClose}
      title="Payment Configuration"
      description="Set up and manage payment methods"
      onSave={handleSaveSettings}
    >
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Basic Payment Methods
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentMethods.cash}
                  onChange={handlePaymentMethodToggle('cash')}
                  disabled // Cash is always enabled
                />
              }
              label="Cash"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentMethods.card}
                  onChange={handlePaymentMethodToggle('card')}
                />
              }
              label="Credit/Debit Card"
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Integration Services
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          {/* Stripe Integration */}
          <Accordion 
            expanded={expanded === 'stripe'} 
            onChange={handleAccordionChange('stripe')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="stripe-content"
              id="stripe-header"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CardIcon sx={{ mr: 1 }} />
                  <Typography>Stripe Payments</Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={paymentMethods.stripe}
                        onChange={handlePaymentMethodToggle('stripe')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label=""
                  />
                  <Chip 
                    size="small"
                    label={stripeSettings.connected ? "Connected" : "Not Connected"}
                    color={stripeSettings.connected ? "success" : "default"}
                    icon={stripeSettings.connected ? <ConnectedIcon /> : <DisconnectedIcon />}
                    sx={{ ml: 1 }}
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Stripe API Key"
                    fullWidth
                    value={stripeSettings.apiKey}
                    onChange={handleStripeSettingChange('apiKey')}
                    placeholder="sk_test_..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ApiKeyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Webhook Secret"
                    fullWidth
                    value={stripeSettings.webhookSecret}
                    onChange={handleStripeSettingChange('webhookSecret')}
                    placeholder="whsec_..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ApiKeyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained"
                    color={stripeSettings.connected ? "success" : "primary"}
                    disabled={!stripeSettings.apiKey.trim()}
                    onClick={handleStripeConnect}
                    fullWidth
                  >
                    {stripeSettings.connected ? "Connected to Stripe" : "Connect to Stripe"}
                  </Button>
                </Grid>
                {!stripeSettings.connected && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      You can find your Stripe API keys in your Stripe Dashboard under Developers &gt; API keys.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          {/* PayNow QR Integration */}
          <Accordion 
            expanded={expanded === 'paynow'} 
            onChange={handleAccordionChange('paynow')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="paynow-content"
              id="paynow-header"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <QrIcon sx={{ mr: 1 }} />
                  <Typography>DBS PayNow QR</Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={paymentMethods.paynow}
                        onChange={handlePaymentMethodToggle('paynow')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label=""
                  />
                  <Chip 
                    size="small"
                    label={paynowSettings.connected ? "Connected" : "Not Connected"}
                    color={paynowSettings.connected ? "success" : "default"}
                    icon={paynowSettings.connected ? <ConnectedIcon /> : <DisconnectedIcon />}
                    sx={{ ml: 1 }}
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Merchant ID"
                    fullWidth
                    value={paynowSettings.merchantId}
                    onChange={handlePaynowSettingChange('merchantId')}
                    placeholder="Your DBS merchant ID"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Terminal ID"
                    fullWidth
                    value={paynowSettings.terminalId}
                    onChange={handlePaynowSettingChange('terminalId')}
                    placeholder="Your terminal ID"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Company Name"
                    fullWidth
                    value={paynowSettings.companyName}
                    onChange={handlePaynowSettingChange('companyName')}
                    placeholder="Your company name (as shown on QR)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained"
                    color={paynowSettings.connected ? "success" : "primary"}
                    disabled={!paynowSettings.merchantId.trim() || !paynowSettings.terminalId.trim()}
                    onClick={handlePaynowConnect}
                    fullWidth
                  >
                    {paynowSettings.connected ? "Connected to PayNow" : "Connect to PayNow"}
                  </Button>
                </Grid>
                {!paynowSettings.connected && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Contact your DBS representative to get your merchant and terminal IDs for PayNow QR integration.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </SettingsDialogBase>
  );
};

export default PaymentDialog;